import http, { request, getBrowserStoredAccessToken, aiHttp } from './http';

export interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface Agent {
  id: string;
  name: string;
  role: string;
  status: string;
}

export interface AgentInvokePayload {
  agentId: string;
  input: string;
  context?: Record<string, unknown>;
}

export interface AgentInvokeResult {
  agentId: string;
  output: string;
  traceId?: string;
}

/** 编排结果中的单步 */
export interface OrchestrationStepResult {
  index: number;
  subGoalDescription: string;
  agentId: string;
  success: boolean;
  output?: unknown;
  errorMessage?: string;
}

/** 编排执行结果 */
export interface OrchestrationResult {
  success: boolean;
  summary: string;
  steps: OrchestrationStepResult[];
  errorMessage?: string;
  goal?: unknown;
}

/** 项目智能助手请求 */
export interface AgentChatRequest {
  conversationId: string;
  message: string;
  projectId?: string;
  /** 客户端请求链路 ID，用于跨前端、网关、AI 服务关联日志 */
  operationId?: string;
  sessionId?: string;
  routeName?: string;
  title?: string;
  /** 透传 ai-service；创建项目页可附组织候选人等 */
  context?: {
    scenario?: string;
    knowledgeBaseHints?: string[];
    systemRequirements?: string[];
    extractedFacts?: Record<string, string>;
    orgMemberRoster?: Array<{
      userId: number;
      realName: string;
      departmentName?: string;
      position?: string;
      platformUserId?: string;
    }>;
    projectCodePatternNotes?: string;
    /** 为 true 时允许将费用/成本/供应链/利润等检索片段送入模型（租户管理员） */
    allowProjectFinancialContext?: boolean;
    /** AI 运行模式：供应商 modes 键（如 flash / pro / lite）或 long_cycle */
    aiRunMode?: AiRunModeId;
    /** 大模型供应商标识，如 deepseek */
    llmProvider?: string;
    /** 当前请求的项目上下文快照（仅用于隔离和诊断） */
    projectId?: string;
    currentProjectId?: string;
    routeName?: string;
    pageTabKey?: string;
    operationId?: string;
  };
  /** 用户上传的截图 base64 编码 */
  imageBase64?: string;
  attachments?: Array<{name: string; url: string; type: string; objectKey?: string}>;
}

export type AiRunModeId = string;

export interface AiChatProviderOption {
  id: string;
  label: string;
  modes: string[];
}

export interface AiChatRunModeOption {
  id: string;
  label: string;
}

export type AiLlmProviderId = 'auto' | string;

export interface AiChatOptionsResponse {
  defaultProvider: string;
  autoProvider?: string;
  bomAutoProvider?: string;
  bomAutoRunMode?: string;
  allowUserModelSelection?: boolean;
  defaultRunMode: AiRunModeId;
  providers: AiChatProviderOption[];
  /** @deprecated 运行模式现由各 provider.modes + longCycleMode 决定 */
  runModes?: AiChatRunModeOption[];
  longCycleMode?: string;
}

/** 本地 ai-service 同步响应 */
export interface AiChatResponse {
  conversation_id: string;
  current_step: string;
  intent?: 'PROJECT' | 'PROFESSIONAL' | 'CHITCHAT' | 'CREATE_PROJECT' | string;
  answer: string;
  message?: string;
  retrieved_data?: unknown;
  analysis_result?: unknown;
  solution_plan?: unknown;
  thought?: string;
  structured_result?: Record<string, unknown>;
  next_questions?: string[];
}

/** 流式步骤事件 */
export interface AiStreamEvent {
  step?: string;
  title?: string;
  status?: string;
  content?: string;
  data?: unknown;
}

/** Agent SSE 流事件：normalizeStreamEvent 归一化后的形状 */
export interface AgentStreamEvent {
  reqId: string;
  dataType?: string;
  content?: string;
  operationCompleted: boolean;
  needRedirect: boolean;
  redirectPageId?: string;
  step?: string;
  title?: string;
  status?: string;
  data?: unknown;
}

/** 项目助手 SSE 流事件（/api/ai/chat/stream） */
export interface AiChatStreamEvent {
  event?: string;
  step?: string;
  title?: string;
  status?: string;
  data?: unknown;
  content?: string;
  answer?: string;
  thought?: unknown;
  structuredResult?: Record<string, unknown>;
  nextQuestions?: string[];
  conversationId?: string;
}

export interface IpdChatResponse {
  reqId: string;
  status: string;
  message?: string;
}

/** 获取 AI 对话框可选的大模型与运行模式 */
export async function fetchAiChatOptions(): Promise<AiChatOptionsResponse> {
  const res = await request<string | AiChatOptionsResponse>(http.get('/ai/chat/options'));
  if (typeof res === 'string') {
    return JSON.parse(res) as AiChatOptionsResponse;
  }
  return res;
}

/** 消息附件（用于持久化与展示） */
export interface ChatMessageAttachmentDTO {
  name: string;
  url: string;
  type: string;
}

export interface ChatMessageDTO {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
  attachments?: ChatMessageAttachmentDTO[];
  thought?: string;
  structuredResult?: Record<string, unknown> | null;
}

/** 会话场景：文本生成 | AI评审 | 项目协助，默认 project_assistance */
export type ConversationScenario = 'text_generation' | 'ai_review' | 'project_assistance';

export interface ConversationSaveRequest {
  scenario: ConversationScenario | string;
  sessionId: string;
  routeName?: string;
  title?: string;
  messages: ChatMessageDTO[];
}

export interface ConversationSummary {
  id: number;
  sessionId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationDetail {
  id: number;
  scenario: string;
  sessionId: string;
  routeName?: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationRenameRequest {
  id: number;
  title: string;
}

export interface SuggestSessionTitleRequest {
  message: string;
  scenario?: string;
  assistantHint?: string;
  routeName?: string;
}

export interface SuggestSessionTitleResponse {
  title: string;
}

export async function getAgents(): Promise<Agent[]> {
  return request<Agent[]>(http.get('/agents'));
}

export async function invokeAgent(payload: AgentInvokePayload): Promise<AgentInvokeResult> {
  return request<AgentInvokeResult>(http.post('/agents/invoke', payload));
}

/** 解析统一 Result 或扁平结构，兼容蛇形字段，避免 reqId 解析不到时误走「只显示 message」分支 */
function normalizeIpdChatPayload(raw: unknown): IpdChatResponse {
  if (raw == null || typeof raw !== 'object') return { reqId: '', status: '' };
  const o = raw as Record<string, unknown>;
  const data = o.data != null && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : o;
  const reqId = String(data.reqId ?? data.req_id ?? data.request_id ?? o.reqId ?? o.req_id ?? o.request_id ?? '');
  const status = String(data.status ?? o.status ?? '');
  const message = String(data.message ?? o.message ?? '');
  return { reqId, status, message: message.trim() ? message : undefined };
}

/** 网关未透传 structured_result 时，从 thought JSON 兜底解析（与 ai-service 一致） */
function extractStructuredFromThought(thought: unknown): Record<string, unknown> | undefined {
  if (thought == null) return undefined;
  try {
    const t = typeof thought === 'string' ? (JSON.parse(thought) as unknown) : thought;
    if (!t || typeof t !== 'object') return undefined;
    const o = t as Record<string, unknown>;
    const direct = o.structured_result ?? o.structuredResult;
    if (direct && typeof direct === 'object') return direct as Record<string, unknown>;
    const cp = o.create_project;
    if (cp && typeof cp === 'object') {
      const sr = (cp as Record<string, unknown>).structured_result ?? (cp as Record<string, unknown>).structuredResult;
      if (sr && typeof sr === 'object') return sr as Record<string, unknown>;
    }
    const prompt = o.create_project_prompt;
    if (prompt && typeof prompt === 'object') {
      const sr = (prompt as Record<string, unknown>).structured_result ?? (prompt as Record<string, unknown>).structuredResult;
      if (sr && typeof sr === 'object') return sr as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/** 提交对话任务到本地 ai-service，同步返回结构化结果 */
export async function postAgentChat(payload: AgentChatRequest): Promise<AiChatResponse> {
  const res = await request<unknown>(aiHttp.post('/ai/chat/sync', payload));
  const raw = res as Record<string, unknown>;
  const data = raw.data != null && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw;
  const answer = String(data.answer ?? data.message ?? raw.answer ?? raw.message ?? '');
  const thought = data.thought ?? raw.thought;
  let structuredResult = data.structured_result ?? data.structuredResult;
  if (structuredResult == null || typeof structuredResult !== 'object') {
    structuredResult = extractStructuredFromThought(thought);
  }
  const nextQuestionsRaw = data.next_questions ?? data.nextQuestions;
  return {
    conversation_id: String(data.conversation_id ?? data.conversationId ?? payload.conversationId ?? ''),
    current_step: String(data.current_step ?? data.currentStep ?? 'done'),
    intent: String(data.intent ?? data.intent_type ?? '').trim() || undefined,
    answer,
    message: answer,
    retrieved_data: data.retrieved_data ?? data.retrievedData,
    analysis_result: data.analysis_result ?? data.analysisResult,
    solution_plan: data.solution_plan ?? data.solutionPlan,
    structured_result: structuredResult && typeof structuredResult === 'object' ? (structuredResult as Record<string, unknown>) : undefined,
    next_questions: Array.isArray(nextQuestionsRaw) ? nextQuestionsRaw.map((v) => String(v)).filter(Boolean) : undefined,
    thought: typeof thought === 'string' ? thought : JSON.stringify(thought ?? {}),
  };
}

export interface ConsumeAgentStreamOptions {
  /** 用于超时中止：传入 AbortController.signal，超时后 controller.abort() 会中断 fetch */
  signal?: AbortSignal;
}

export interface ConsumeAiChatStreamOptions {
  signal?: AbortSignal;
}

function normalizeAiChatStreamEvent(raw: Record<string, unknown>, sseEventName?: string): AiChatStreamEvent {
  const eventType = String(sseEventName ?? raw.event ?? '').trim();
  if (eventType === 'final' || String(raw.step ?? '') === 'final') {
    const finalBody = raw;
    const nextRaw = finalBody.nextQuestions ?? finalBody.next_questions;
    return {
      event: 'final',
      conversationId: String(
        finalBody.conversationId ?? finalBody.conversation_id ?? raw.conversationId ?? '',
      ),
      answer: String(finalBody.answer ?? finalBody.message ?? raw.answer ?? ''),
      thought: finalBody.thought ?? raw.thought,
      structuredResult:
        (finalBody.structuredResult ?? finalBody.structured_result ?? raw.structuredResult) as
          | Record<string, unknown>
          | undefined,
      nextQuestions: Array.isArray(nextRaw)
        ? nextRaw.map((v) => String(v)).filter(Boolean)
        : undefined,
    };
  }
  const stepId = String(raw.step ?? eventType ?? 'step');
  const title = String(raw.title ?? stepId);
  const status = String(raw.status ?? '');
  return {
    event: 'step',
    step: stepId,
    title,
    status,
    data: raw.data,
    content: typeof raw.content === 'string' ? raw.content : undefined,
  };
}

function dispatchStreamEvent(onEvent: (event: AiChatStreamEvent) => void, event: AiChatStreamEvent) {
  queueMicrotask(() => onEvent(event));
}

/**
 * 消费结果流：按 reqId 订阅 SSE，每收到一条事件调用 onEvent，结束时 resolve。
 * 返回 Promise，在 operationCompleted 为 true 或流关闭时 resolve；出错时 reject。
 * 支持传入 options.signal 以便外部超时中止（如 45 秒无任何返回时 abort）。
 */
/**
 * 消费项目助手流式对话：POST /api/ai/chat/stream，按 step / final 事件回调。
 */
export function consumeAiChatStream(
  payload: AgentChatRequest,
  onEvent: (event: AiChatStreamEvent) => void,
  options?: ConsumeAiChatStreamOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getBrowserStoredAccessToken();
    const base = http.defaults.baseURL || '/api';
    const url = `${base}/ai/chat/stream`;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        conversationId: payload.conversationId,
        message: payload.message,
        projectId: payload.projectId ?? '',
        operationId: payload.operationId,
        context: payload.context,
        imageBase64: payload.imageBase64 ?? "",
        attachments: payload.attachments ?? [],
      }),
    };
    if (options?.signal) fetchOptions.signal = options.signal;
    fetch(fullUrl, fetchOptions)
      .then((res) => {
        if (!res.ok) {
          res.text().then((t) => reject(new Error(t || `HTTP ${res.status}`)));
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          resolve();
          return;
        }
        const decoder = new TextDecoder();
        let buf = '';
        let sseEventName: string | undefined;
        function parseDataLine(rest: string) {
          const json = rest.trim();
          if (!json || json === '[DONE]' || json === '"[DONE]"') return;
          try {
            const raw = JSON.parse(json) as Record<string, unknown>;
            dispatchStreamEvent(onEvent, normalizeAiChatStreamEvent(raw, sseEventName));
          } catch {
            /* ignore */
          }
        }
        function flushCompleteLines(streamDone: boolean) {
          const lines = buf.split('\n');
          buf = streamDone ? '' : (lines.pop() ?? '');
          for (const line of lines) {
            if (!line) {
              sseEventName = undefined;
              continue;
            }
            if (line.startsWith('event:')) {
              sseEventName = line.slice(6).trim();
              continue;
            }
            const m = line.match(/^data:\s?(.*)$/);
            if (m) parseDataLine(m[1] ?? '');
          }
          if (streamDone && buf.trim()) {
            for (const line of buf.split('\n')) {
              if (line.startsWith('event:')) sseEventName = line.slice(6).trim();
              const m = line.match(/^data:\s?(.*)$/);
              if (m) parseDataLine(m[1] ?? '');
            }
          }
        }
        function pump(): Promise<void> {
          return reader.read().then(({ done, value }) => {
            if (done) {
              flushCompleteLines(true);
              resolve();
              return;
            }
            buf += decoder.decode(value, { stream: true });
            flushCompleteLines(false);
            return pump();
          });
        }
        pump().catch(reject);
      })
      .catch(reject);
  });
}

export function consumeAgentStream(
  reqId: string,
  onEvent: (event: AgentStreamEvent) => void,
  options?: ConsumeAgentStreamOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getBrowserStoredAccessToken();
    const url = `${http.defaults.baseURL}/agent/stream/${encodeURIComponent(reqId)}`;
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const fetchOptions: RequestInit = { headers, credentials: 'include' };
    if (options?.signal) fetchOptions.signal = options.signal;
    fetch(fullUrl, fetchOptions)
      .then((res) => {
        if (!res.ok) {
          res.text().then((t) => reject(new Error(t || `HTTP ${res.status}`)));
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          resolve();
          return;
        }
        const decoder = new TextDecoder();
        let buf = '';
        function normalizeStreamEvent(raw: Record<string, unknown>): AgentStreamEvent {
          const content =
            typeof raw.content === 'string'
              ? raw.content
              : typeof raw.text === 'string'
                ? raw.text
                : typeof raw.delta === 'string'
                  ? raw.delta
                  : undefined;
          const innerData = raw.data;
          let parsedData: unknown = innerData;
          if (typeof innerData === 'string') {
            const trimmed = innerData.trim();
            if (trimmed) {
              try {
                parsedData = JSON.parse(trimmed);
              } catch {
                parsedData = innerData;
              }
            }
          }
          const parsedObj = typeof parsedData === 'object' && parsedData != null ? (parsedData as Record<string, unknown>) : null;
          const rawEvent = typeof raw.event === 'string' ? raw.event : undefined;
          const step =
            typeof raw.step === 'string'
              ? raw.step
              : rawEvent && rawEvent !== 'step'
                ? rawEvent
                : parsedObj && typeof parsedObj.step === 'string'
                  ? String(parsedObj.step)
                  : rawEvent === 'step' && parsedObj && typeof parsedObj.step === 'string'
                    ? String(parsedObj.step)
                    : undefined;
          return {
            reqId:
              typeof raw.reqId === 'string'
                ? raw.reqId
                : typeof raw.req_id === 'string'
                  ? raw.req_id
                  : parsedObj && typeof parsedObj.reqId === 'string'
                    ? String(parsedObj.reqId)
                    : '',
            dataType:
              typeof raw.dataType === 'string'
                ? raw.dataType
                : typeof raw.data_type === 'string'
                  ? raw.data_type
                  : rawEvent,
            content,
            operationCompleted: Boolean(raw.operationCompleted ?? raw.operation_completed ?? parsedObj?.operationCompleted ?? parsedObj?.operation_completed),
            needRedirect: Boolean(raw.needRedirect ?? raw.need_redirect ?? parsedObj?.needRedirect ?? parsedObj?.need_redirect),
            redirectPageId:
              typeof raw.redirectPageId === 'string'
                ? raw.redirectPageId
                : typeof raw.redirect_page_id === 'string'
                  ? raw.redirect_page_id
                  : parsedObj && typeof parsedObj.redirectPageId === 'string'
                    ? String(parsedObj.redirectPageId)
                    : parsedObj && typeof parsedObj.redirect_page_id === 'string'
                      ? String(parsedObj.redirect_page_id)
                      : undefined,
            step,
            title:
              typeof raw.title === 'string'
                ? raw.title
                : parsedObj && typeof parsedObj.title === 'string'
                  ? String(parsedObj.title)
                  : undefined,
            status:
              typeof raw.status === 'string'
                ? raw.status
                : parsedObj && typeof parsedObj.status === 'string'
                  ? String(parsedObj.status)
                  : undefined,
            data: parsedObj && 'data' in parsedObj ? (parsedObj.data as unknown) : parsedData,
          };
        }
        function parseDataLine(rest: string) {
          const json = rest.trim();
          if (!json || json === '[DONE]' || json === '"[DONE]"') return;
          try {
            const raw = JSON.parse(json) as Record<string, unknown>;
            onEvent(normalizeStreamEvent(raw));
          } catch (_) {
            /* ignore parse error */
          }
        }
        function flushCompleteLines(streamDone: boolean) {
          if (!streamDone) {
            const lines = buf.split('\n');
            buf = lines.pop() ?? '';
            for (const line of lines) {
              if (!line) continue;
              const m = line.match(/^data:\s?(.*)$/);
              if (m) parseDataLine(m[1] ?? '');
            }
          } else {
            for (const line of buf.split('\n')) {
              if (!line) continue;
              const m = line.match(/^data:\s?(.*)$/);
              if (m) parseDataLine(m[1] ?? '');
            }
            buf = '';
          }
        }
        function pump(): Promise<void> {
          return reader.read().then(({ done, value }) => {
            if (done) {
              flushCompleteLines(true);
              resolve();
              return;
            }
            buf += decoder.decode(value, { stream: true });
            flushCompleteLines(false);
            return pump();
          });
        }
        pump().catch(reject);
      })
      .catch(reject);
  });
}

const AGENT_CONVERSATION_HTTP_TIMEOUT_MS = 120_000;

/** 保存 / 更新一段会话到后端数据库 */
export async function saveAgentConversation(payload: ConversationSaveRequest): Promise<number> {
  const res = await request<Result<number>>(
    http.post('/agent/conversations', payload, { timeout: AGENT_CONVERSATION_HTTP_TIMEOUT_MS }),
  );
  if (!res.success) throw new Error(res.message || '保存会话失败');
  return res.data ?? 0;
}

/** 查询当前用户某场景下的会话摘要列表 */
export async function getAgentConversationList(scenario: string): Promise<ConversationSummary[]> {
  const res = await request<Result<ConversationSummary[]>>(
    http.get('/agent/conversations', { params: { scenario }, timeout: AGENT_CONVERSATION_HTTP_TIMEOUT_MS }),
  );
  if (!res.success) throw new Error(res.message || '查询会话列表失败');
  return res.data ?? [];
}

/** 获取单个会话的完整详情（含消息 JSON） */
export async function getAgentConversationDetail(id: number): Promise<ConversationDetail> {
  const res = await request<Result<ConversationDetail>>(
    http.get(`/agent/conversations/${id}`, { timeout: AGENT_CONVERSATION_HTTP_TIMEOUT_MS }),
  );
  if (!res.success || !res.data) throw new Error(res.message || '获取会话详情失败');
  return res.data;
}

export async function renameAgentConversation(payload: ConversationRenameRequest): Promise<void> {
  await request<Result<unknown>>(http.patch('/agent/conversations/title', payload));
}

/** 跨场景 LLM 概括会话标题（≤15 字），不依赖意图路由 */
export async function suggestAgentSessionTitle(
  payload: SuggestSessionTitleRequest,
): Promise<string> {
  const res = await request<Result<SuggestSessionTitleResponse>>(
    http.post('/agent/conversations/suggest-title', payload, { timeout: 30_000 }),
  );
  if (!res.success) throw new Error(res.message || '生成会话标题失败');
  return String(res.data?.title ?? '').trim();
}

export async function deleteAgentConversation(id: number): Promise<void> {
  await request<Result<unknown>>(http.delete(`/agent/conversations/${id}`));
}

